import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { MessageCircle, X, ArrowLeft, Loader2, Send, Check, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trackEvent } from "@/hooks/useEventTracking";
import { PrivacyNotice } from "@/components/PrivacyNotice";

type Category = "bug" | "update" | "idea" | "other";

const CATEGORIES: { id: Category; label: string; emoji: string; needsSub: boolean }[] = [
  { id: "update", label: "Aktualizacja danych schroniska", emoji: "📝", needsSub: true },
  { id: "bug", label: "Zgłoś błąd", emoji: "🐛", needsSub: true },
  { id: "idea", label: "Pomysł / sugestia", emoji: "💡", needsSub: false },
  { id: "other", label: "Inne / współpraca", emoji: "🤝", needsSub: false },
];

const SUBCATEGORIES: Record<Category, string[]> = {
  bug: [
    "Błędne dane schroniska",
    "Niedziałający link",
    "Problem techniczny",
    "Literówka / błąd w treści",
    "Inne",
  ],
  update: [
    "Aktualizacja profilu schroniska/fundacji",
    "Dodanie nowego miejsca na mapie",
    "Zgłoszenie zamkniętego lub nieaktywnego miejsca",
    "Inna sprawa",
  ],
  idea: [],
  other: [],
};

const STORAGE_KEY = "help_widget_minimized_v1";
const MINIMIZE_DAYS = 7;

const isMinimizedFresh = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < MINIMIZE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const HelpWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [minimized, setMinimized] = useState<boolean>(() => isMinimizedFresh());
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("help_widget_open_v1") === "1";
    } catch {
      return false;
    }
  });

  // Persistuj stan otwarcia panelu, żeby po odświeżeniu zachować widok
  useEffect(() => {
    try {
      if (open) localStorage.setItem("help_widget_open_v1", "1");
      else localStorage.removeItem("help_widget_open_v1");
    } catch {}
  }, [open]);
  const [cookieDecided, setCookieDecided] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem("gdzieschronisko_cookie_consent");
    } catch {
      return true;
    }
  });
  const [step, setStep] = useState<"category" | "subcategory" | "form" | "done">("category");
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<string>("");
  const [shelterName, setShelterName] = useState("");
  const [shelterId, setShelterId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedWithoutEmail, setSavedWithoutEmail] = useState(false);

  useEffect(() => {
    const onChange = () => {
      try {
        setCookieDecided(!!localStorage.getItem("gdzieschronisko_cookie_consent"));
      } catch {
        setCookieDecided(true);
      }
    };
    const onReset = () => setCookieDecided(false);
    window.addEventListener("cookie-consent-change", onChange);
    window.addEventListener("cookie-consent-reset", onReset);
    return () => {
      window.removeEventListener("cookie-consent-change", onChange);
      window.removeEventListener("cookie-consent-reset", onReset);
    };
  }, []);

  // Programmatic open via window event (e.g. "Zgłoś problem z profilem" button)
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const cat: Category = (detail.category as Category) || "bug";
      setOpen(true);
      setMinimized(false);
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      setCategory(cat);
      if (detail.shelterId) setShelterId(detail.shelterId);
      if (detail.shelterName) setShelterName(detail.shelterName);
      if (detail.subcategory) {
        setSubcategory(detail.subcategory);
        setStep("form");
      } else if (SUBCATEGORIES[cat].length > 0) {
        setStep("subcategory");
      } else {
        setStep("form");
      }
      trackEvent("Czat - otwarcie z profilu", { shelter_id: detail.shelterId, category: cat });
    };
    window.addEventListener("open-help-widget", onOpen);
    return () => window.removeEventListener("open-help-widget", onOpen);
  }, []);

  // Ukryj w panelu admina (po hookach, by uniknąć "Rendered more hooks than previous render")
  const isAdminPath = location.pathname.startsWith("/admin");
  const isPanelPath = location.pathname.startsWith("/panel");

  // Auto-uzupełnianie nazwy schroniska na podstawie URL
  const detectedShelterSlug = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith("/schronisko/") || path.startsWith("/zglos/")) {
      return params.slug || path.split("/")[2] || "";
    }
    return "";
  }, [location.pathname, params.slug]);

  useEffect(() => {
    if (!detectedShelterSlug || shelterName) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("shelters")
        .select("id,name")
        .eq("slug", detectedShelterSlug)
        .maybeSingle();
      if (!cancelled && data?.name) {
        setShelterName(data.name);
        setShelterId(data.id);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedShelterSlug]);

  // Gdy widoczna jest sekcja hero - wymuszamy zwiniętą formę (samo kółko)
  const [inHero, setInHero] = useState(false);
  useEffect(() => {
    let io: IntersectionObserver | null = null;
    let raf = 0;
    const attach = () => {
      const el = document.querySelector("[data-hero-section]") as HTMLElement | null;
      if (!el) {
        setInHero(false);
        return;
      }
      io?.disconnect();
      io = new IntersectionObserver(
        ([entry]) => setInHero(entry.isIntersecting && entry.intersectionRatio > 0.15),
        { threshold: [0, 0.15, 0.5] }
      );
      io.observe(el);
    };
    attach();
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(attach);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      io?.disconnect();
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [location.pathname]);

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    trackEvent("Czat - otwarcie", { path: location.pathname });
  };

  const handleMinimize = () => {
    setOpen(false);
    setMinimized(true);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
    resetForm();
  };

  const resetForm = () => {
    setStep("category");
    setCategory(null);
    setSubcategory("");
    setMessage("");
    setEmail("");
    setSavedWithoutEmail(false);
    // shelterName zachowujemy (auto-fill z URL)
  };

  const pickCategory = (cat: Category) => {
    setCategory(cat);
    trackEvent("Czat - kategoria", { category: cat });
    if (SUBCATEGORIES[cat].length > 0) {
      setStep("subcategory");
    } else {
      setStep("form");
    }
  };

  const pickSubcategory = (sub: string) => {
    setSubcategory(sub);
    trackEvent("Czat - podkategoria", { category, subcategory: sub });
    setStep("form");
  };

  const goBack = () => {
    if (step === "form" && category && SUBCATEGORIES[category].length > 0) {
      setStep("subcategory");
    } else if (step === "form" || step === "subcategory") {
      setStep("category");
      setCategory(null);
      setSubcategory("");
    }
  };

  const isUpdateHint =
    category === "update" &&
    subcategory === "Aktualizacja profilu schroniska/fundacji";
  const isAddHint =
    category === "update" &&
    subcategory === "Dodanie nowego miejsca na mapie";
  const showSelfServiceHint = isUpdateHint || isAddHint;
  const selfServicePath = isAddHint ? "/dodaj-schronisko" : "/aktualizuj-schronisko";
  const selfServiceIntro = isAddHint
    ? "Najłatwiej dodać nowe miejsce samodzielnie. Wybierz ścieżkę: załóż konto opiekuna schroniska albo zgłoś jednorazowo."
    : "Zaktualizuj dane samodzielnie. Wybierz ścieżkę: zaloguj się jako opiekun albo zgłoś zmianę jako użytkownik.";
  const selfServiceCta = isAddHint
    ? "Przejdź do dodawania schroniska →"
    : "Przejdź do aktualizacji danych →";

  const handleSubmit = async () => {
    if (!category) return;
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      toast.error("Wiadomość musi mieć co najmniej 5 znaków");
      return;
    }
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Nieprawidłowy email");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-feedback", {
        body: {
          category,
          subcategory: subcategory || null,
          message: trimmed,
          email: email.trim() || null,
          shelter_name: shelterName.trim() || null,
          shelter_id: shelterId,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      setSavedWithoutEmail(!email.trim());
      setStep("done");
      trackEvent("Czat - wyslane", {
        category,
        subcategory: subcategory || null,
        has_email: !!email.trim(),
        has_shelter: !!shelterName.trim(),
      });
    } catch (err: any) {
      toast.error(err?.message || "Nie udało się wysłać wiadomości");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== RENDER =====

  // Ukryj w panelu admina i w panelu organizacji (tam jest dedykowany PanelHelpWidget)
  if (isAdminPath || isPanelPath) return null;
  if (!cookieDecided && !open) return null;

  // Pill / mała ikonka - jeden element morfowany przez CSS, bez remountu
  if (!open) {
    const compact = minimized || inHero;
    return (
      <div
        className={`fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-[2000] flex items-center transition-[gap] duration-300 ease-out ${
          compact ? "gap-0" : "gap-1"
        }`}
        style={{ WebkitBackfaceVisibility: "hidden", backfaceVisibility: "hidden" }}
      >
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Otwórz pomoc"
          className={`group flex items-center bg-gradient-to-br from-primary to-[hsl(155_55%_28%)] text-primary-foreground rounded-full shadow-glow-soft hover:shadow-glow font-semibold text-[11px] sm:text-sm overflow-hidden transition-[padding,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 ${
            compact
              ? "gap-0 px-1.5 py-1.5 sm:px-2 sm:py-2"
              : "gap-1.5 sm:gap-2 pl-2.5 pr-3 py-1.5 sm:pl-4 sm:pr-5 sm:py-2.5"
          }`}
          style={{
            transform: "translateZ(0)",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            willChange: "padding, transform",
          }}
        >
          <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors shrink-0">
            <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </span>
          <span
            className={`whitespace-nowrap transition-[max-width,opacity,margin] duration-300 ease-out ${
              compact ? "max-w-0 opacity-0 ml-0" : "max-w-[160px] opacity-100"
            }`}
            style={{
              transform: "translateZ(0)",
              WebkitFontSmoothing: "antialiased",
              willChange: "max-width, opacity",
            }}
          >
            Pomoc / zgłoś
          </span>
        </button>
        <button
          type="button"
          onClick={handleMinimize}
          aria-label="Zminimalizuj"
          tabIndex={compact ? -1 : 0}
          className={`rounded-full bg-background/95 backdrop-blur border border-border/80 text-muted-foreground hover:text-foreground shadow-sm flex items-center justify-center overflow-hidden transition-[width,height,opacity,transform] duration-300 ease-out ${
            compact
              ? "w-0 h-0 opacity-0 scale-50 pointer-events-none"
              : "w-5 h-5 sm:w-7 sm:h-7 opacity-100 scale-100"
          }`}
          style={{
            transform: compact ? "translateZ(0) scale(0.5)" : "translateZ(0) scale(1)",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            willChange: "width, height, opacity, transform",
            borderColor: compact ? "transparent" : undefined,
          }}
        >
          <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </button>
      </div>
    );
  }

  // Otwarte okno
  return (
    <div className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-[2100] w-[calc(100vw-1.5rem)] sm:w-[340px] max-h-[calc(100vh-1.5rem)] bg-card/95 backdrop-blur-xl border border-border/60 rounded-2xl shadow-elevated flex flex-col overflow-hidden animate-scale-in">
      {/* Header */}
      <div className="relative flex items-center justify-between px-3 sm:px-3.5 py-2 sm:py-2.5 bg-gradient-to-br from-primary via-primary to-[hsl(155_55%_28%)] text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: 'radial-gradient(at 20% 0%, hsl(0 0% 100% / 0.4) 0px, transparent 50%), radial-gradient(at 80% 100%, hsl(155 60% 20% / 0.5) 0px, transparent 50%)'
        }} />
        <div className="relative flex items-center gap-2">
          {step !== "category" && step !== "done" && (
            <button
              type="button"
              onClick={goBack}
              aria-label="Wstecz"
              className="hover:bg-white/15 rounded-full p-0.5 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 backdrop-blur">
            <MessageCircle className="w-3 h-3" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-[13px] tracking-tight">
              {step === "done" ? "Wysłano" : "Jak możemy pomóc?"}
            </span>
            {step === "category" && (
              <span className="text-[10px] text-white/75 font-medium">Odpowiadamy w 24h</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Zamknij"
          className="relative hover:bg-white/15 rounded-full p-1 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3">
        {step === "category" && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Wybierz temat</p>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pickCategory(c.id)}
                className="group w-full text-left p-2 rounded-xl border border-border/70 bg-card hover:border-primary/40 hover:bg-accent/40 hover:shadow-sm transition-all flex items-center gap-2.5"
              >
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-accent/60 group-hover:bg-primary/10 text-base transition-colors shrink-0" aria-hidden>
                  {c.emoji}
                </span>
                <span className="flex-1 font-semibold text-xs text-foreground">{c.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        )}

        {step === "subcategory" && category && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {category === "bug" ? "Czego dotyczy błąd?" : "Wybierz, co najlepiej pasuje"}
            </p>
            {SUBCATEGORIES[category].map((sub) => (
              <button
                key={sub}
                type="button"
                onClick={() => pickSubcategory(sub)}
                className="group w-full text-left px-2.5 py-2 rounded-lg border border-border/70 bg-card hover:border-primary/40 hover:bg-accent/40 hover:shadow-sm transition-all text-xs font-medium flex items-center justify-between gap-2"
              >
                <span>{sub}</span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>
            ))}
          </div>
        )}

        {step === "form" && category && (
          <div className="space-y-2.5 sm:space-y-3">
            {subcategory && (
              <div className="text-[11px] sm:text-xs text-muted-foreground">
                Wybrano: <span className="font-medium text-foreground">{subcategory}</span>
              </div>
            )}

            {showSelfServiceHint && (
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5 sm:p-3 text-[11px] sm:text-xs text-foreground space-y-1.5 sm:space-y-2">
                <p>💡 {selfServiceIntro}</p>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(selfServicePath);
                  }}
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                >
                  {selfServiceCta}
                </button>
                <p className="text-muted-foreground pt-1 border-t border-primary/20">
                  Jeśli masz problem z formularzem albo wolisz napisać do nas bezpośrednio, skorzystaj z formularza poniżej.
                </p>
              </div>
            )}

            {(category === "bug" || category === "update") && (
              <ShelterSearchField
                value={shelterName}
                onChange={(name, id) => {
                  setShelterName(name);
                  setShelterId(id);
                }}
              />
            )}

            <div className="space-y-1">
              <Label htmlFor="hw-message" className="text-[11px] sm:text-xs">
                Wiadomość *
              </Label>
              <Textarea
                id="hw-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Opisz krótko, o co chodzi…"
                rows={4}
                maxLength={4000}
                className="text-base sm:text-sm min-h-[80px] sm:min-h-[120px]"
              />
              <p className="text-[10px] sm:text-[11px] text-muted-foreground">
                {message.trim().length < 5
                  ? `Wpisz co najmniej 5 znaków (${message.trim().length}/5).`
                  : `${message.trim().length}/4000 znaków.`}
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="hw-email" className="text-[11px] sm:text-xs">
                Twój e-mail (opcjonalnie)
              </Label>
              <Input
                id="hw-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                maxLength={255}
                className="h-9 sm:h-10 text-base sm:text-sm"
              />
              <p className="text-[10px] sm:text-[11px] text-muted-foreground">
                Bez adresu e-mail przyjmiemy zgłoszenie, ale nie będziemy mogli Ci odpisać.
              </p>
            </div>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || message.trim().length < 5}
              className="w-full h-9 sm:h-10 text-xs sm:text-sm"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
              )}
              Wyślij
            </Button>

            <PrivacyNotice className="text-[10px] sm:text-[11px]" />
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-6 space-y-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-7 h-7 text-primary" />
            </div>
            <p className="font-semibold">Dzięki! Wiadomość do nas dotarła.</p>
            {savedWithoutEmail ? (
              <p className="text-xs text-muted-foreground px-2">
                ⚠️ Nie zostawiłeś/aś maila - nie będziemy mogli odpisać.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Odezwiemy się najszybciej jak się da.</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                resetForm();
                setOpen(false);
              }}
              className="mt-2"
            >
              Zamknij
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Wyszukiwarka schroniska =====

type ShelterHit = { id: string; name: string; city: string };

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");

// Warianty z polskimi znakami dla pojedynczych liter ASCII -
// pomaga gdy uzytkownik wpisuje "lodz" zamiast "łódź".
const PL_VARIANTS: Record<string, string> = {
  a: "ą", c: "ć", e: "ę", l: "ł", n: "ń", o: "ó", s: "ś", z: "ż",
};

const polishVariants = (token: string): string[] => {
  const out = new Set<string>([token]);
  for (let i = 0; i < token.length && out.size < 6; i++) {
    const alt = PL_VARIANTS[token[i]];
    if (alt) out.add(token.slice(0, i) + alt + token.slice(i + 1));
  }
  return [...out];
};

// Krotka odleglosc Levenshteina (do tolerancji literowek)
const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1).fill(0).map((_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prevDiag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = a[i - 1] === b[j - 1]
        ? prevDiag
        : Math.min(prevDiag, prev[j], prev[j - 1]) + 1;
      prevDiag = tmp;
    }
  }
  return prev[b.length];
};

const scoreHit = (hit: ShelterHit, tokens: string[]): number => {
  const name = normalize(hit.name);
  const city = normalize(hit.city);
  const words = (name + " " + city).split(/\s+/).filter(Boolean);
  let score = 0;
  for (const tk of tokens) {
    let tokenScore = 0;
    if (name.includes(tk) || city.includes(tk)) {
      tokenScore = 10;
      if (name.startsWith(tk) || city.startsWith(tk)) tokenScore += 4;
      else if (words.some((w) => w.startsWith(tk))) tokenScore += 2;
    } else if (tk.length >= 4) {
      const minD = Math.min(...words.map((w) => levenshtein(w, tk)));
      if (minD <= 1) tokenScore = 6;
      else if (minD <= 2 && tk.length >= 6) tokenScore = 3;
    }
    if (tokenScore === 0) return -1; // brak pokrycia tokenu
    score += tokenScore;
  }
  return score;
};

const ShelterSearchField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string, id: string | null) => void;
}) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<ShelterHit[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const tokens = normalize(q).split(/\s+/).filter((t) => t.length >= 2);
      if (!tokens.length) {
        setResults([]);
        setLoading(false);
        return;
      }
      // Buduj warianty z polskimi znakami dla ilike (DB jest case-insensitive
      // ale rozroznia diakrytyki, wiec dodajemy odmiany).
      const orParts: string[] = [];
      for (const tk of tokens) {
        for (const v of polishVariants(tk)) {
          const safe = v.replace(/[%,()]/g, "");
          if (!safe) continue;
          orParts.push(`name.ilike.%${safe}%`);
          orParts.push(`city.ilike.%${safe}%`);
        }
      }
      const { data } = await supabase
        .from("shelters")
        .select("id,name,city")
        .or(orParts.join(","))
        .limit(40);
      if (cancelled) return;
      const scored = ((data || []) as ShelterHit[])
        .map((h) => ({ h, s: scoreHit(h, tokens) }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s || a.h.name.localeCompare(b.h.name, "pl"))
        .slice(0, 8)
        .map((x) => x.h);
      setResults(scored);
      setLoading(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
      setLoading(false);
    };
  }, [query]);

  return (
    <div className="space-y-1 relative">
      <Label htmlFor="hw-shelter" className="text-[11px] sm:text-xs">
        Nazwa schroniska/fundacji (opcjonalnie)
      </Label>
      <div className="relative">
        <Input
          id="hw-shelter"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            onChange(e.target.value, null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Wpisz nazwę miejsca lub miasto..."
          maxLength={200}
          autoComplete="off"
          className={`h-9 sm:h-10 text-base sm:text-sm ${query ? "pr-8" : ""}`}
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQuery("");
              setResults([]);
              setOpen(false);
              onChange("", null);
            }}
            aria-label="Wyczyść wybór schroniska"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && query.trim().length >= 2 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Szukam…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Brak wyników - możesz wpisać nazwę ręcznie.
            </div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setQuery(r.name);
                  onChange(r.name, r.id);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 flex items-center justify-between gap-2"
              >
                <span className="font-medium truncate">{r.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">{r.city}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default HelpWidget;
